/**
 * AppContext & AppProvider
 * =========================
 * A global context provider for managing dynamic key-value pairs
 * across a React application using React Context API.
 *
 * Supports:
 * - Adding or updating any key-value pair
 * - Reading global values anywhere in the app
 *
 * -------------------------
 *
 * -------------------------
 * Hook Usage
 * -------------------------
 * Import `useAppContext()` in any component:
 *
 * import { useAppContext } from './AppContext';
 *
 * const { globals, setAppKey} = useAppContext();
 *
 * -------------------------
 * Example: Set or Update Value
 * -------------------------
 * setAppKey('theme', 'dark');
 * setAppKey('username', 'Navin');
 * setAppKey('isAuthenticated', true);
 *
 * -------------------------
 * Example: Read a Value
 * -------------------------
 * const theme = globals.theme;
 * const isLoggedIn = globals.isAuthenticated;
 *
 *
 */

import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react'

interface AccountSetting {
  scope: string[]
  effective_value: string | boolean | number
  user_scope: string[]
  app_scope: string[]
}

interface AccountSettings {
  [key: string]: AccountSetting
}

interface GlobalState {
  accSettings?: AccountSettings
  [key: string]: any 
}
interface AppContextType {
  globals: GlobalState
  setAppKey: (key: string, value: any) => void
}

// Create context with default undefined
const AppContext = createContext<AppContextType | undefined>(undefined)

// Provider component
interface AppProviderProps {
  children: ReactNode
  initialState?: GlobalState
}

export const AppProvider: React.FC<AppProviderProps> = ({ children, initialState = {} }) => {
  const [globals, setGlobals] = useState<GlobalState>(initialState)

  const setAppKey = useCallback((key: string, value: any) => {
    setGlobals((prev) => ({ ...prev, [key]: value }))
  }, [])

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      globals,
      setAppKey,
    }),
    [globals, setAppKey]
  )

  return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
}

// Hook to consume context
export const useAppContext = (): AppContextType => {
  const context = useContext(AppContext)
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider')
  }
  return context
}
